import { ref, computed, defineAsyncComponent, watch, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { useSpotStore } from '@/stores/SpotStore';
import { useUserStore } from '@/stores/UserStore';
import { useAlertStore } from '@/stores/AlertStore';
import { useOnsiteReportsStore } from '@/stores/OnsiteReportsStore';
import { useProForecastStore } from '@/stores/ProForecastStore';
import { useBulletinStore } from '@/stores/BulletinStore';
import useStorageSystem, { updateStorageSystem } from '@/composables/useStorageSystem';
import useLocalStorage from '@/composables/useLocalStorage'
import useWebviewStorage from '@/composables/useWebviewStorage';
import { useMediaQuery } from '@/composables/useMediaQuery';
import useSpotGlobals from '@/composables/useSpotGlobals';
import useSettingsGlobals from '@/composables/useSettingsGlobals'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'


const { $IS_ARBITRARY_SPOT } = useSpotGlobals();
const globalSettings = useSettingsGlobals();
const { $WEB_VIEW } = globalSettings;  
const { $BRAND } = useConfigurationGlobals()

export let useCompleteReportStore = defineStore('completeReportStore', () => {
    const spotStore = useSpotStore();
    const userStore = useUserStore();
    const alertStore = useAlertStore();
    const onsiteReportsStore = useOnsiteReportsStore();
    const proForecastStore = useProForecastStore();
    const bulletinStore = useBulletinStore();

    const isDesktop = useMediaQuery('(min-width: 840px)');

    const defaultContainers = [101, 100, 1, 2, 11, 3, 4, 10, 6, 5, 7, 8, 9];
    
    const showCompleteReportSyncControl = Object.prototype.hasOwnProperty.call(globalSettings, 'show_complete_report_sync_control') ? globalSettings.show_complete_report_sync_control : false;

    const upgradeToMemberLevels = {
      '-1': ['plus', 'pro', 'gold'],
      2: ['plus', 'pro', 'gold'],
      46: ['pro', 'gold'],
      49: ['gold']
    }    

    const isLoading = ref(true);
    const error = ref(null);
    const containers = ref([]);
    const isArbitrarySpot = ref($IS_ARBITRARY_SPOT);
    const showSettingsModal = ref(false);
    const windGraphStartingViewSettingId= ref('crwindGraphStartingView');
    const proForecastStartingViewSettingId = ref('crProForecastStartingView');
    const currentConditionsStartingViewSettingId = ref('crCurrentConditionsStartingView');

    const syncSettings = $WEB_VIEW ? useWebviewStorage(true, 'crSyncSettings') : useLocalStorage(true, 'crSyncSettings');

    const storageType = computed(() => syncSettings.value ? 'apiUserStorage' : $WEB_VIEW ? 'off' : 'localStorage');

    const [activeContainers] = useStorageSystem(defaultContainers, 'crActiveContainers', storageType.value);
    const [containerOrder, crContainerOrderLoaded] = useStorageSystem(defaultContainers, 'crContainerOrder', storageType.value);
    const [lastUpdateTime] = useStorageSystem('timestamp', 'crLastUpdateTime', storageType.value);
    const [windGraphStartingView] = useStorageSystem('basic', windGraphStartingViewSettingId.value, storageType.value);
    const [proForecastStartingView] = useStorageSystem('collapsed', proForecastStartingViewSettingId.value, storageType.value);
    const [currentConditionsStartingView] = useStorageSystem('condensed', currentConditionsStartingViewSettingId.value, storageType.value);

    const storedData = computed(() => ({
      crActiveContainers: activeContainers.value,
      crContainerOrder: containerOrder.value,
      crLastUpdateTime: lastUpdateTime.value,
      [windGraphStartingViewSettingId.value]: windGraphStartingView.value,
      [proForecastStartingViewSettingId.value]: proForecastStartingView.value,
      [currentConditionsStartingViewSettingId.value]: currentConditionsStartingView.value
    }));

    const storedDataWebView = computed(() => {
      let data = [];

      for (const key in storedData.value) {
        data.push(`${key}:${storedData.value[key]}`);
      }

      return data.join(';');
    });

    const webViewMessenger = ref(null);
    const webViewMessengerUnwatch = ref(null);

    const requests = computed(() => {
      if (error.value) return;

      let promises = [];

      if (spotStore.hasSpotProduct(10)) promises.push(alertStore.getAdvisoryByLatLon(spotStore.lat, spotStore.lon));
      if (spotStore.hasSpotProduct(4)) promises.push(onsiteReportsStore.getOnsiteReports(spotStore.lat, spotStore.lon));
      if (spotStore.hasSpotProduct(6)) promises.push(bulletinStore.getBulletins(spotStore.lat, spotStore.lon));
      if (showProForecast.value) promises.push(proForecastStore.getProForecast(spotStore.proForecastIdIfExists))

      return promises;
    });

    const showProForecast = computed(() => spotStore.hasSpotProduct(3) && activeContainers.value.includes(3) && proForecastStore.canShowProForecast);
    const showOnsiteReports = computed(() => spotStore.hasSpotProduct(4) && activeContainers.value.includes(4) && onsiteReportsStore.hasOnsiteReports);
    const showAdvisories = computed(() => spotStore.hasSpotProduct(10) && activeContainers.value.includes(101) && alertStore.hasAlerts);
    const showMarineForecasts = computed(() => spotStore.hasSpotProduct(6) && activeContainers.value.includes(6) && bulletinStore.hasBulletins );
    const showNearbyTides = computed(() => spotStore.hasSpotProduct(5) && activeContainers.value.includes(5));
    const showWindGraph = computed(() => spotStore.hasSpotProduct(1) && activeContainers.value.includes(1) && !isArbitrarySpot.value && ! spotStore.isNowcast);
    const showForecastTable = computed(() => spotStore.hasSpotProduct(2) && activeContainers.value.includes(2));
    const showArchiveData = computed(() => spotStore.hasSpotProduct(7) && activeContainers.value.includes(7));
    const showStatsData = computed(() => spotStore.hasSpotProduct(8) && activeContainers.value.includes(8));
    const showInfoData = computed(() => spotStore.hasSpotProduct(9) && activeContainers.value.includes(9));
    const showCurrentConditions = computed(() => activeContainers.value.includes(100));
    const showGorgeWindbow = computed(() => spotStore.hasSpotProduct(11));   
    const showWindGraphStartingView = computed(() => isDesktop.value && !$WEB_VIEW);    
    const showCurrentConditionsStartingView = computed(() => !isDesktop.value)    
    const showPremiumModelUpgradeBlock = computed(() => spotStore.modelUpgradeAvailable && !spotStore.proForecastIdIfExists);    
    const showPremiumProModelUpgradeBlock = computed(() => spotStore.modelUpgradeAvailable && spotStore.proForecastIdIfExists);    
    const showProModelUpgradeBlock = computed(() => userStore.memberLevel >= 46 && userStore.memberLevel < 49 && spotStore.proForecastIdIfExists && $BRAND.toLowerCase() !== 'windalert');    
    const showModelUpgradeBlock = computed(() => showPremiumModelUpgradeBlock.value || showProModelUpgradeBlock.value);
    const upgradeBlockText = computed(() => showProModelUpgradeBlock.value ? 'all <strong>Pro Models</strong>' : 'all <strong>Premium Models</strong>');

    const upgrade = computed(() => {
      let upgradeTo = userStore.memberLevel ? upgradeToMemberLevels[userStore.memberLevel] : []
      let freeTrial = userStore.memberLevel ? userStore.memberLevel === -1 : null

      return [spotStore.upgradeAvailable, upgradeTo, freeTrial]
    });

    const upgradeSpot = computed(() => {
      if (!spotStore.isPro) return upgrade.value;

      let [upgradeAvailable, upgradeTo, freeTrial] = upgrade.value;

      if (userStore.memberLevel <= 46) {
        upgradeTo = upgradeToMemberLevels[46];
      }

      return [upgradeAvailable, upgradeTo, freeTrial]
    })

    const getContainers = computed(() => containers.value);
    
    const userChangedSettings = computed(() => {
      let hasChanged = false;
      
      if (windGraphStartingView.value !== 'basic' || proForecastStartingView.value !== 'collapsed' || currentConditionsStartingView.value !== 'condensed') {
        hasChanged = true;
      };

      for (let i = 0; i < defaultContainers.length; i++) {
        const container = defaultContainers[i];

        if (!activeContainers.value.includes(container)) hasChanged = true;
        if (containerOrder.value.indexOf(container) !== i) hasChanged = true
      }
        
      return hasChanged;
    });

    const components = {
      1: {
        component: defineAsyncComponent(
          () => import('@/components/WindGraphsDataContainerSection.vue')
        ),
        settings: [
          {
            id: windGraphStartingViewSettingId.value,
            label: 'Starting Graph',
            type: 'segmentedControl',
            model: windGraphStartingView,
            defaultValue: windGraphStartingView.value,
            options: [
              { value: 'basic', label: 'Basic' },
              { value: 'detailed', label: 'Detailed' }
            ],
            show: () => showWindGraphStartingView.value
          }
        ],
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Wind Graph', upgrade: upgradeSpot },
        show: () => showWindGraph.value
      },
      2: {
        component: defineAsyncComponent(
          () => import('@/components/ForecastTableDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Forecast Table', upgrade: upgrade },
        show: () => showForecastTable.value
      },
      3: {
        component: defineAsyncComponent(
          () => import('@/components/ProForecastDataContainerSection.vue')
        ),
        settings: [
          {
            id: proForecastStartingViewSettingId.value,
            label: 'Starting View',
            type: 'segmentedControl',
            model: proForecastStartingView,
            defaultValue: proForecastStartingView.value,
            options: [
              { value: 'collapsed', label: 'Collapsed' },
              { value: 'expanded', label: 'Expanded' }
            ],
            show: () => true
          }
        ],
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Pro Forecast', memberLevel: userStore.memberLevel },
        show: () => showProForecast.value
      },
      4: {
        component: defineAsyncComponent(
          () => import('@/components/OnsiteReportsDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Onsite Reports', upgrade: upgrade },
        show: () => showOnsiteReports.value
      },
      5: {
        component: defineAsyncComponent(
          () => import('@/components/NearbyTidesDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Nearby Tides', upgrade: upgrade },
        show: () => showNearbyTides.value
      },
      6: {
        component: defineAsyncComponent(
          () => import('@/components/MarineForecastDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Marine Forecast', upgrade: upgrade },
        show: () => showMarineForecasts.value
      },
      7: {
        component: defineAsyncComponent(
          () => import('@/components/ArchiveDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Archive Data', upgrade: upgrade },
        show: () => showArchiveData.value
      },
      8: {
        component: defineAsyncComponent(() => import('@/components/StatsDataContainerSection.vue')),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Stats', upgrade: upgrade },
        show: () => showStatsData.value
      },
      9: {
        component: defineAsyncComponent(
          () => import('@/components/stationInfoDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Station Info', upgrade: upgrade },
        show: () => showInfoData.value
      },
      10: {
        component: defineAsyncComponent(
          () => import('@/components/AdvisoriesDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Advisories', upgrade: upgrade },
        show: () => showAdvisories.value
      },
      11: {
        component: defineAsyncComponent(
          () => import('@/components/GorgeWindbowDataContainerSection.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Gorge Windbow', upgrade: upgrade },
        show: () => showGorgeWindbow.value
      },
      100: {
        component: defineAsyncComponent(
          () => import('@/components/CurrentConditionsContainerSection.vue')
        ),
        settings: [
          {
            id: currentConditionsStartingViewSettingId.value,
            label: 'Starting View',
            type: 'segmentedControl',
            model: currentConditionsStartingView,
            defaultValue: currentConditionsStartingView.value,
            options: [
              { value: 'condensed', label: 'Condensed' },
              { value: 'expanded', label: 'Expanded' }
            ],
            show: () => showCurrentConditionsStartingView.value
          }
        ],
        allowReorder: true,
        allowHidden: true,
        props: {
          title: 'Current Conditions',
          containerTypeId: 'Current Conditions',
          upgrade: upgrade
        },
        show: () => showCurrentConditions.value
      },
      101: {
        component: defineAsyncComponent(() => import('@/components/AlertWidget.vue')),
        allowReorder: true,
        allowHidden: true,
        props: { title: 'Advisory Overview' },
        show: () => showAdvisories.value
      },
      1000: {
        component: defineAsyncComponent(() => import('@/components/UpgradeBlock.vue')),
        allowReorder: true,
        allowHidden: true,
        props: { productText: upgradeBlockText, upgrade: upgrade },
        show: () => showModelUpgradeBlock.value
      },
      1001: {
        component: defineAsyncComponent(
          () => import('@/components/UpgradeBlockPremiumProModels.vue')
        ),
        allowReorder: true,
        allowHidden: true,
        props: { upgrade: upgrade },
        show: () => showPremiumProModelUpgradeBlock.value
      },
      1100: {
        component: defineAsyncComponent(() => import('@/components/BannerAd.vue')),
        allowReorder: true,
        allowHidden: true,
        props: { type: 'mid' },
        show: () => true
      }
    }    

    const addContainer = (container) => containers.value.push(container);
    const insertContainer = (container, index) => containers.value.splice(index, 0, container);
    const clearContainers = () => containers.value = [];
    const resetContainers = () => {
      activeContainers.value = [...defaultContainers];
      containerOrder.value = [...defaultContainers];
      windGraphStartingView.value = 'basic';
      proForecastStartingView.value = 'collapsed';
      currentConditionsStartingView.value = 'condensed';
    };

    const updateLoadingStatus = (newStatus) => isLoading.value = newStatus;

    function containerCheck() {
      const newContainers = [];
      const mergedContainers = [...containerOrder.value];
      let updateStorage = false;

      defaultContainers.forEach((container) => {
        if (!mergedContainers.includes(container)) {
          newContainers.push(container);
          updateStorage = true;
        };
      });

      mergedContainers.forEach((container, i) => {
        if (!defaultContainers.includes(container)) {
          mergedContainers.splice(i, 1);
          updateStorage = true;
        };
      })

      if (newContainers.length > 0) {
        newContainers.forEach((container) => {
          const defaultIndex = defaultContainers.indexOf(container);
          const defaultPrevContainer = defaultContainers[defaultIndex - 1];
          const defaultNextContainer = defaultContainers[defaultIndex + 1];
          let insertIndex = mergedContainers.length - 1;

          if (mergedContainers.includes(defaultPrevContainer)) {
            insertIndex = mergedContainers.indexOf(defaultPrevContainer) + 1
          }
          else if (mergedContainers.includes(defaultNextContainer)) {
            insertIndex = mergedContainers.indexOf(defaultNextContainer)

          }
          else if (mergedContainers.includes(9) && mergedContainers.indexOf(9) === mergedContainers.length - 1) {
            insertIndex = mergedContainers.indexOf(9)
          }

          mergedContainers.splice(insertIndex, 0, container);
          activeContainers.value.push(container);
        });
      }

      if (updateStorage) containerOrder.value = [...mergedContainers];
    }

    function updateStorage() {
      for (const key in storedData.value) {
        const value = JSON.stringify(storedData.value[key]);

        if (storageType.value === 'apiUserStorage') {
          userStore.updateAPIStorageItem(key, value)
        } else {
          window.localStorage.setItem(key, value)
        }
      }
    }

    watch(syncSettings, (val) => {
      if ($WEB_VIEW) return;

      updateStorage()
      updateStorageSystem(storageType.value);
    });

    watchEffect(() => {
      if (!crContainerOrderLoaded.value) return;
      
      containerCheck();
    });

    // watchEffect(() => {
    //   if (!$WEB_VIEW) return;

    //   if (syncSettings.value) {
    //     if (typeof webViewMessengerUnwatch.value === 'function') webViewMessengerUnwatch.value();
    //     updateStorage();
    //     updateStorageSystem(storageType.value);
        
    //     return;
    //   }

    //   [webViewMessenger.value, webViewMessengerUnwatch.value] = useStorageSystem(storedDataWebView, 'crSettings', 'webViewMessenger')
    // })

    return {
      showCompleteReportSyncControl,
      containers,
      activeContainers,
      containerOrder,
      lastUpdateTime,
      components,
      requests,
      isLoading,
      error,
      currentConditionsStartingView,
      windGraphStartingView,
      proForecastStartingView,
      showSettingsModal,
      syncSettings,
      getContainers,
      userChangedSettings,
      addContainer,
      insertContainer,
      clearContainers,
      resetContainers,
      containerCheck,
      updateLoadingStatus,
    }
});
